import { cx, formatNumber } from "../utils/format";

export interface StackedBar { label: string; ai: number; people: number; }

/** Tickets per day or week, split into what the AI answered on its own (the AI hue, its output) and what people handled.
 *  Labels thin out by themselves once there are too many bars to name each one. The copy-and-charts round (part four): each column shows
 *  its numbers in the kit's tooltip (`data-tip`) on hover and on keyboard focus, and is a keyboard stop spoken as those words; the chart is a
 *  named group, since a picture (`role="img"`) would hide its focusable columns from screen readers. */
export function StackedBars({ data, legend }: { data: StackedBar[]; legend: { ai: string; people: string } }) {
  const max = Math.max(...data.map((d) => d.ai + d.people), 1);
  const every = Math.ceil(data.length / 10);
  return (
    <figure className="ob-bars-figure">
      <div className="ob-bars" role="group" aria-label={`${legend.people} and ${legend.ai}, ${data.length} bars`}>
        {data.map((d, i) => {
          const tip = stackedTip(d, legend);
          return (
            <div className="ob-bars__col" key={d.label} data-tip={tip} role="img" aria-label={tip} tabIndex={0}>
              <span className="ob-bars__stack" style={{ ["--v" as string]: ((d.ai + d.people) / max) * 100 }}>
                {d.ai > 0 && <span className="ob-bars__bar ob-bars__bar--ai" style={{ flexGrow: d.ai }} />}
                <span className="ob-bars__bar" style={{ flexGrow: d.people }} />
              </span>
              <span className="ob-bars__label" aria-hidden="true">{(data.length - 1 - i) % every === 0 ? d.label : ""}</span>
            </div>
          );
        })}
      </div>
      <figcaption className="ob-legend"><span className="ob-legend__item"><span className="ob-legend__key" />{legend.people}</span><span className="ob-legend__item"><span className="ob-legend__key ob-legend__key--ai" />{legend.ai}</span></figcaption>
    </figure>
  );
}

/** a column's words: "28.10: 44 handled by people, 19 answered by the AI alone" (only the legend's first letter goes lower case, so "AI" stays) */
const stackedTip = (d: StackedBar, legend: { ai: string; people: string }) => `${d.label}: ${d.people} ${lowerFirst(legend.people)}, ${d.ai} ${lowerFirst(legend.ai)}`;
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** One bar cut into parts that add up to a whole, with the parts named below it: what became of the drafts. */
export function ShareBar({ parts }: { parts: { id: string; label: string; value: number; tone?: "ai" | "strong" | "mid" | "soft" | "faint" }[] }) {
  const total = parts.reduce((n, p) => n + p.value, 0) || 1;
  return (
    <div className="ob-share">
      <div className="ob-share__bar" role="img" aria-label={parts.map((p) => `${p.label}: ${Math.round((p.value / total) * 100)} %`).join(", ")}>
        {parts.filter((p) => p.value > 0).map((p) => <span key={p.id} className={cx("ob-share__part", `ob-share__part--${p.tone ?? "mid"}`)} style={{ flexGrow: p.value }} />)}
      </div>
      <ul className="ob-share__list">
        {parts.map((p) => (
          <li key={p.id} className="ob-share__row">
            <span className={cx("ob-share__key", `ob-share__part--${p.tone ?? "mid"}`)} /><span className="ob-share__label">{p.label}</span>
            <span className="ob-share__value">{formatNumber(p.value)}</span><span className="ob-share__pct">{Math.round((p.value / total) * 100)} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A quiet horizontal meter inside a table cell or a row: a share of the row's maximum. */
export function RowMeter({ value, tone }: { value: number; tone?: "ai" }) {
  return <span className={cx("ob-rowmeter", tone === "ai" && "ob-rowmeter--ai")} style={{ ["--v" as string]: Math.round(Math.max(0, Math.min(1, value)) * 100) }} aria-hidden="true" />;
}

/** Tickets by weekday and hour. The copy-and-charts round (part four): each cell names its day, hour and count in the kit's tooltip on hover
 *  ("Tu 14:00: 12 tickets"); cells are no keyboard stops (168 of them; the table and its numbers already read out). */
export function Heatmap({ days, rows }: { days: string[]; rows: { hour: string; values: number[] }[] }) {
  const max = Math.max(...rows.flatMap((r) => r.values), 1);
  return (
    <table className="ob-heat">
      <thead><tr><th></th>{days.map((d) => <th key={d}>{d}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.hour}>
            <td className="ob-heat__hour">{r.hour}</td>
            {r.values.map((v, i) => <td key={i} className="ob-heat__cell" data-tip={heatTip(days[i] ?? "", r.hour, v)} style={{ ["--h" as string]: (v / max).toFixed(2) }}>{v === 0 ? "—" : v}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** a heatmap cell's words: "Tu 14:00: 12 tickets", "1 ticket", "no tickets" */
export const heatTip = (day: string, hour: string, n: number) => `${day} ${hour}: ${n === 0 ? "no tickets" : n === 1 ? "1 ticket" : `${formatNumber(n)} tickets`}`;
